import React, { useEffect, useMemo, useState } from 'react'
import template from 'lodash/template'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { Grid, Card, CardContent } from '@mui/material'
import Select from 'components/forms/Select'
import RadioButtons from '../../../components/forms/RadioButtons'
import { CITIES, DATA_INTEGRATION } from 'traitements/constants/TraitementConstants'
import { numberList } from 'utils/ListUtil'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import { sample, without } from 'lodash'

const RECURRENCE_TYPE_WEEKLY = 'weekly'
const RECURRENCE_TYPE_MONTHLY = 'monthly'

const ParamTraitementImport = ({
    choiceTypeCode,
    setChoiceTypeCode,
    listCode,
    jobtype,
    valid,
    orobnatIsUsed,
    pair,
    setPairToSave,
    setListHour,
}) => {
    const dispatch = useDispatch()

    const {
        contributors,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)


    const nom = pair[jobtype]

    const [filters, setFilters] = useState({})

    useEffect(() => {
        setFilters({ stationsMode: choiceTypeCode })
    }, [choiceTypeCode])


    const changeFilters = changes => setFilters({ ...filters, ...changes })


    const [days, setDays] = useState('6')
    const [monthDay, setMonthDay] = useState('1')
    const [recurrence, setRecurrence] = useState(RECURRENCE_TYPE_WEEKLY)
    const [hours, setHours] = useState(null)
    const [minutes, setMinutes] = useState(0)

    useEffect(() => {
        setListHour(prev => {
            if (prev.length === 0) return prev

            const value = sample(prev)

            setHours(value)

            return without(prev, value)
        })
    }, [])

    const cron = useMemo(() => {
        const dayOfMonth = recurrence === RECURRENCE_TYPE_MONTHLY? monthDay: '*'
        const dayOfWeek = recurrence === RECURRENCE_TYPE_MONTHLY? '*': days
        return ['0', minutes, hours, dayOfMonth, '*', dayOfWeek, '*'].join(' ')
    }, [ recurrence, monthDay, days, hours, minutes])

    const initialJob = useMemo(() => ({
        id: null,
        name: nom,
        parameters: {
            codeMode: choiceTypeCode,
            stationIntegration: true,
            codes: listCode,
            dataIntegration: DATA_INTEGRATION,
        },
        jobType: jobtype,
        cron,
        days,
        hours,
        minutes,
    }), [nom, choiceTypeCode, listCode, jobtype, cron, days, hours, minutes])

    useEffect(() => {
        if (!choiceTypeCode) {
            setChoiceTypeCode(CITIES)
        }
    }, [])

    const hoursOptions = useMemo(() => [ { value: '*', label: i18n.everyHours }, ...numberList(24) ], [])
    const minutesOptions = useMemo(() => [
        { value: '*/5', label: template(i18n.everyCustomMinutes)({ minute: 5 }) },
        { value: '*/10', label: template(i18n.everyCustomMinutes)({ minute: 10 }) },
        { value: '*/15', label: template(i18n.everyCustomMinutes)({ minute: 15 }) },
        { value: '*/20', label: template(i18n.everyCustomMinutes)({ minute: 20 }) },
        { value: '*/30', label: template(i18n.everyCustomMinutes)({ minute: 30 }) },
        ...numberList(60),
    ], [])

    const daysOptions = useMemo(() => [
        { value: '*', label: i18n.allDays },
        { value: '1', label: i18n.monday },
        { value: '2', label: i18n.tuesday },
        { value: '3', label: i18n.wednesday, disabled: true },
        { value: '4', label: i18n.thursday },
        { value: '5', label: i18n.friday },
        { value: '6', label: i18n.saturday },
        { value: '0', label: i18n.sunday },
    ], [])

    const monthDayOptions = useMemo(() => numberList(32).slice(1), [])

    const recurrenceOptions = [
        { code: RECURRENCE_TYPE_WEEKLY, name: i18n.weekly },
        { code: RECURRENCE_TYPE_MONTHLY, name: i18n.monthly },
    ]

    useEffect(() => {
        if (valid || !jobtype) {
            return
        }

        const updatedJob = {
            listCode,
            initialJob,
            jobtype,
            filters,
            dispatch,
        }

        setPairToSave(prev => {
            if (!jobtype) return prev
            return {
                ...prev,
                [jobtype]: updatedJob,
            }
        })
    }, [valid, hours, minutes, recurrence, days, monthDay, filters.producer, nom, listCode, jobtype])

    useEffect(() => {
        if (!valid || !jobtype) {
            return
        }

        setPairToSave(prev => {
            // eslint-disable-next-line no-unused-vars
            const { [jobtype]: deletedValue, ...next } = prev
            return next
        })
    }, [valid, jobtype])


    return (
        <Card>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid size={3}>
                        { i18n.recurrence }
                        <RadioButtons
                            disabled={valid}
                            elements={ recurrenceOptions }
                            selected={ recurrence }
                            onChange={ (v) => setRecurrence(v) }
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            disabled={valid}
                            value={ hours || 0 }
                            options={ hoursOptions }
                            label={ i18n.jobHour }
                            onChange={(v) => setHours(v)}
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            disabled={valid}
                            value={ minutes || 0 }
                            options={ minutesOptions }
                            label={ i18n.jobMinute }
                            onChange={ (v) => setMinutes(v) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            disabled={valid}
                            value={ recurrence === RECURRENCE_TYPE_MONTHLY ? monthDay : days }
                            options={ recurrence === RECURRENCE_TYPE_MONTHLY ? monthDayOptions : daysOptions }
                            label={ recurrence === RECURRENCE_TYPE_MONTHLY ? i18n.day : i18n.dayOfWeek }
                            onChange={(v) => recurrence === RECURRENCE_TYPE_MONTHLY ? setMonthDay(v) : setDays(v) }
                            noNullValue
                            noSort
                        />
                    </Grid>
                </Grid>
                {orobnatIsUsed &&
                <MultiContributorsAutocomplete
                    options={contributors}
                    label={i18n.producer}
                    onChange={(id) => changeFilters({ producer: id || undefined })}
                    values={filters.producer}
                    keyLabel='labelDisplay'
                    displayWithCode
                    obligatory
                />}
            </CardContent>
        </Card>
    )
}


ParamTraitementImport.propTypes = {
    choiceTypeCode: PropTypes.string,
    setChoiceTypeCode: PropTypes.func,
    listCode: PropTypes.any,
    jobtype: PropTypes.string,
    valid: PropTypes.bool,
    orobnatIsUsed: PropTypes.bool,
    pair: PropTypes.any,
    setPairToSave: PropTypes.func,
    setListHour: PropTypes.func,
}


export default ParamTraitementImport